"use client";

import { GLOBAL_SEARCH_API } from "@/lib/constants";
import { cn, debounce } from "@/lib/utils";
import { useCountryContext } from "@/providers/country-provider";
import { TSearchResults } from "@/types/search";
import { Loader2Icon, SearchIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

type GlobalSearchProps = {
	classNames?: {
		container?: string;
		inputContainer?: string;
		input?: string;
	};
	country?: string;
};

type TStyle = {
	[key: string]: string;
};

const styles: TStyle = {
	COMMUNITY: "text-teal-600 border border-teal-200 bg-teal-100",
	CHANNEL: "text-purple-500 border border-purple-200 bg-purple-100",
	PRODUCT: "text-blue-500 border border-blue-200 bg-blue-100",
	PRODUCTV2: "text-amber-600 border border-amber-200 bg-amber-100",
	"VIP MEMBER": "text-white bg-gradient-to-r from-[#B78316] via-[#dfb106] to-[#cd9319]",
	"VERIFIED MEMBER": "text-orange-500 border border-orange-200 bg-orange-100",
	POST: "text-rose-500 border border-rose-200 bg-rose-100",
	EVENT: "text-emerald-500 border border-emerald-200 bg-emerald-100",
	CATEGORY: "text-fuchsia-500 border border-fuchsia-200 bg-fuchsia-100"
};

const GlobalSearch = ({ classNames, country }: GlobalSearchProps) => {
	const { zeroStateSearch } = useCountryContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [query, setQuery] = useState<string>("");
	const [searchResults, setSearchResults] = useState<TSearchResults>([]);
	const [isFocused, setIsFocused] = useState(false);

	const handleSearch = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const { value } = e.target;

			setQuery(value);

			if (value.length < 2) {
				setSearchResults([]);
				return;
			}

			setIsLoading(true);

			const url = new URL(GLOBAL_SEARCH_API);
			url.searchParams.set("query", value);
			if (country) {
				url.searchParams.set("country", country);
			}

			const response = await fetch(url, {
				next: {
					revalidate: 300,
				},
			});

			const { data } = await response.json();

			setSearchResults(data);
		} catch (error) {
			console.error("Error occurred while fetching search results");
		} finally {
			setIsLoading(false);
		}
	}, 300);

	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleBlur = (event: React.FocusEvent) => {
		const relatedTarget = event.relatedTarget as HTMLElement | null;
		if (relatedTarget && relatedTarget.closest(".dropdown-container")) {
			return;
		}
		setIsFocused(false);
	};

	return (
		<div className={cn("group relative", classNames?.container)}>
			<div
				className={cn(
					"group flex min-w-max flex-1 items-center bg-white px-4 py-4 shadow-products-search",
					"transition-colors duration-150 ease-linear focus-within:border-brand-primary",
					classNames?.inputContainer,
					isFocused && query === "" ? "rounded-tl-3xl rounded-tr-3xl" : "rounded-3xl"
				)}
			>
				<span>
					<SearchIcon className="h-4 w-4 text-gray-400 transition-colors duration-150 ease-linear group-focus-within:text-blue-500" />
				</span>
				<input
					id="search"
					type="search"
					name="search"
					placeholder="What are you looking for?"
					className={cn("w-full px-2 text-sm focus:outline-none", classNames?.input)}
					autoComplete="off"
					onChange={handleSearch}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
			</div>

			{/* Suggestions Dropdown */}
			{isFocused && (
				<div
					className={cn(
						"customized-scrollbar dropdown-container absolute left-0 top-full z-10 max-h-80 w-full overflow-y-scroll bg-white p-4 text-left shadow",
						"rounded rounded-bl-xl rounded-br-xl"
					)}
					onMouseDown={(event) => event.preventDefault()}
				>
					<ul>
						{query === "" && zeroStateSearch ? (
							zeroStateSearch.map((section, sectionIndex) => (
								<div key={sectionIndex} className="mb-4">
									<h2 className="mb-2 text-xl font-bold text-[#1A2133]">
										{section.title.charAt(0).toUpperCase() + section.title.slice(1)}
									</h2>
									<ul>
										{section.values.map((suggestion, suggestionIndex) => (
											<li key={suggestionIndex} className="mb-1">
												<Link
													href={suggestion.link}
													className="text-sm text-gray-800 hover:text-blue-600 hover:underline"
												>
													{suggestion.name}
												</Link>
											</li>
										))}
									</ul>
									{sectionIndex < zeroStateSearch.length - 1 && (
										<hr className="mt-4 border-gray-300" />
									)}
								</div>
							))
						) : isLoading ? (
							<Loader2Icon className="mx-auto h-8 w-8 animate-spin text-brand-primary" />
						) : query !== "" && searchResults.length > 0 ? (
							searchResults.map((result) => (
								<li
									key={result.id}
									className="group mb-2 block truncate border-b p-1 pb-2 text-sm text-gray-600 last:mb-0 last:border-b-0 last:pb-0"
								>
									<Link
										href={result.href}
										className="block hover:text-brand-primary focus:text-brand-primary"
									>
										<div className="flex items-center justify-between gap-4">
											<span className="line-clamp-1 block max-w-xs truncate">{result.title}</span>
											<span
												className={`rounded-md px-1 py-0.5 text-xxs font-medium group-hover:opacity-90 ${
													styles[result.type]
												}`}
											>
												{result.label ? result.label : result.type}
											</span>
										</div>
									</Link>
								</li>
							))
						) : query !== "" && searchResults.length === 0 ? (
							<li className="text-sm text-gray-400">No results found</li>
						) : null}
					</ul>
				</div>
			)}
		</div>
	);
};

export default GlobalSearch;
